/* eslint-disable @typescript-eslint/no-explicit-any */
// ============= Imports =============

import { AuthContextProps } from '@/contexts/AuthContext';
import { tokenManager } from '@tw/tw-component-library';

export interface SubmitWorkflowParams {
  id: number;
  sectionKey: string;
  type: number;
  submittedData: any;
  getAccessToken: AuthContextProps['getAccessToken'];
  spoofedUser: string | null;
}

export interface SubmitWorkflowResponse {
  success: boolean;
  data?: unknown;
  error?: string;
  errors?: Record<string, string>;
  status?: number;
}

// ============= Constants =============
const BASE_URL = import.meta.env.VITE_BASE_URL;
const API_ENDPOINT = '/api/Workflow';
const AXIS_SCOPE = 'api://f23d5e95-70a8-4aca-b373-e23d8c9f6454/access_as_user';
const HEADERS = { 'Content-Type': 'application/json' };

/**
 * Creates headers for API request
 * @param token Authentication token
 * @returns Headers object
 */
const createHeaders = (token: string, spoofedUser: string | null) => ({
  Authorization: `Bearer ${token}`,
  ...HEADERS,
  ...(spoofedUser && {
    'x-Spoof-User': spoofedUser,
  }),
});

// ============= Main Function =============
/**
 * Submits workflow data to the API
 * @param params Parameters for workflow submission
 * @returns Promise with API response containing submission result or error
 */
export const submitWorkflow = async ({
  id,
  sectionKey,
  type,
  submittedData,
  getAccessToken,
  spoofedUser,
}: SubmitWorkflowParams): Promise<SubmitWorkflowResponse> => {
  try {
    console.log('🔄 Submitting API data...');

    // Token acquisition with required scopes
    const token = await getAccessToken([AXIS_SCOPE]);
    if (!token) {
      return {
        success: false,
        error: 'Failed to acquire API token',
      };
    }
    tokenManager.logTokenInfo(token, 'API');

    // URL construction
    const url = `${BASE_URL}${API_ENDPOINT}/${id}/sections/${sectionKey}/save?type=${type}&submit=true`;
    const convertURI = encodeURI(url);
    console.log('🌐 Making request to:', convertURI);

    // API request
    const response = await fetch(convertURI, {
      method: 'POST',
      body: JSON.stringify(submittedData),
      headers: createHeaders(token, spoofedUser || null),
    });

    // Response handling
    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      const fieldErrors: Record<string, string> = {};

      // Try to get more detailed error information from response
      try {
        const contentType = response.headers.get('content-type') || '';
        let errorData: unknown = null;
        if (contentType.includes('application/json')) {
          errorData = await response.json();
        } else {
          // Handle plain text error bodies
          errorData = await response.text();
        }

        if (typeof errorData === 'string') {
          const trimmed = errorData.trim();
          if (trimmed) {
            errorMessage = `${errorMessage}: ${trimmed}`;
          }
        } else if (errorData && typeof errorData === 'object') {
          const asRecord = errorData as Record<string, unknown>;
          if (typeof asRecord.message === 'string' && asRecord.message) {
            errorMessage = `${errorMessage}: ${asRecord.message}`;
          }
          // Extract field errors if present
          if (asRecord.errors && typeof asRecord.errors === 'object') {
            const errorsObj = asRecord.errors as Record<string, unknown>;
            Object.entries(errorsObj).forEach(([field, messages]) => {
              if (Array.isArray(messages)) {
                fieldErrors[field] = messages.join(' ');
              } else if (typeof messages === 'string') {
                fieldErrors[field] = messages;
              }
            });
          }
        }
      } catch {
        // If we can't parse the error response, use the status text
        errorMessage = `${errorMessage}: ${
          response.statusText || 'Unknown error'
        }`;
      }

      console.log('❌ API request failed:', errorMessage, fieldErrors);
      return {
        success: false,
        error: errorMessage,
        errors: fieldErrors,
        status: response.status,
      };
    }

    // Parse successful response
    let data: unknown;
    try {
      data = await response.json();
    } catch {
      console.warn('⚠️ Response parsing failed, treating as empty response');
      data = null;
    }

    console.log('✅ API data submitted successfully');
    return {
      success: true,
      data,
    };
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'An unknown error occurred';
    console.error('🔴 Error submitting API data:', errorMessage);
    return {
      success: false,
      error: errorMessage,
    };
  }
};
